/**
 * Helpers for typing a `DD.MM.YYYY` date by hand (BaseDateInput).
 *
 * Users may omit leading zeros (`8.5.1970`); the day and month segments are padded
 * to two digits as soon as the separating `.` is typed, and the year accepts exactly
 * four digits. See claude/specs/DataEntryImprovement (VAM-294).
 */

const LENIENT_DATE_REGEX = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

/**
 * Parse `D.M.YYYY` / `DD.MM.YYYY` to a LOCAL-midnight `Date`, or `null` when the text
 * is malformed or names a non-existent day (e.g. `31.02.2024` — no rollover).
 */
export function parseLenientGermanDate(value: string): Date | null {
  const match = LENIENT_DATE_REGEX.exec(value.trim());
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
    return date;
  }
  return null;
}

/** Format a `Date` as `DD.MM.YYYY`. */
export function formatGermanDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

/**
 * Normalize text while it is being typed: treat `,` `/` `-` and spaces as the `.`
 * separator (mobile decimal keypads offer `,` only), keep only digits and dots, pad a one-digit
 * day/month to two digits once its trailing `.` is typed (`8.` → `08.`), allow at most
 * two dots, and cap the segments at 2/2/4 digits.
 */
export function autoPadDateSegment(value: string): string {
  const segments = value
    .replace(/[,/\s-]/g, '.')
    .replace(/[^\d.]/g, '')
    .split('.')
    .slice(0, 3);

  return segments
    .map((segment, index) => {
      const isLast = index === segments.length - 1;
      if (index === 2) return segment.slice(0, 4);
      const trimmed = segment.slice(0, 2);
      return !isLast && trimmed.length === 1 ? `0${trimmed}` : trimmed;
    })
    .join('.');
}
